import { MAX_MENU_DEPTH, type MenuItem, type MenuNode } from "../types/menu"

/**
 * Chuyển đổi giữa mảng phẳng Strapi lưu (`parent` = cấp, `order` = vị trí) và
 * cây dùng cho antd Tree. Toàn bộ hàm ở đây đều thuần: nhận cây vào, trả cây
 * mới ra — không sửa tại chỗ, để undo/redo chỉ cần giữ tham chiếu cũ.
 */

export const buildTree = (items: MenuItem[] | null | undefined): MenuNode[] => {
  if (!Array.isArray(items) || items.length === 0) return []

  const ordered = [...items].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
  const roots: MenuNode[] = []
  // stack[i] là node gần nhất ở cấp i, dùng để gắn con vào đúng cha.
  const stack: MenuNode[] = []

  ordered.forEach(item => {
    const node: MenuNode = {
      id: item.id,
      name: item.name || "",
      slug: item.slug || "",
      url: item.url || item.slug || "#",
      children: [],
    }

    // Cấp không được nhảy cóc: item cấp 2 mà chưa có cha cấp 1 thì kéo về cấp hợp lệ.
    const depth = Math.min(Math.max(item.parent ?? 0, 0), stack.length, MAX_MENU_DEPTH - 1)

    if (depth === 0) {
      roots.push(node)
    } else {
      stack[depth - 1].children.push(node)
    }

    stack.length = depth
    stack.push(node)
  })

  return roots
}

export const flattenTree = (nodes: MenuNode[]): MenuItem[] => {
  const items: MenuItem[] = []

  const walk = (list: MenuNode[], depth: number) => {
    list.forEach(node => {
      items.push({
        id: node.id,
        name: node.name,
        slug: node.slug,
        url: node.url || node.slug || "#",
        parent: depth,
        order: items.length + 1,
      })
      walk(node.children, depth + 1)
    })
  }

  walk(nodes, 0)
  return items
}

export const countNodes = (nodes: MenuNode[]): number =>
  nodes.reduce((total, node) => total + 1 + countNodes(node.children), 0)

export const findNode = (nodes: MenuNode[], id: number): MenuNode | null => {
  for (const node of nodes) {
    if (node.id === id) return node
    const found = findNode(node.children, id)
    if (found) return found
  }
  return null
}

/** Cấp của node (0-based), -1 nếu không tìm thấy. */
export const findDepth = (nodes: MenuNode[], id: number, depth = 0): number => {
  for (const node of nodes) {
    if (node.id === id) return depth
    const found = findDepth(node.children, id, depth + 1)
    if (found >= 0) return found
  }
  return -1
}

/** Danh sách nằm cùng cấp với `id`, kèm chỉ số của nó trong danh sách đó. */
const locate = (nodes: MenuNode[], id: number): { siblings: MenuNode[]; index: number; parent: MenuNode | null } | null => {
  const index = nodes.findIndex(node => node.id === id)
  if (index >= 0) return { siblings: nodes, index, parent: null }

  for (const node of nodes) {
    const found = locate(node.children, id)
    if (found) return { ...found, parent: found.parent ?? node }
  }
  return null
}

const mapTree = (nodes: MenuNode[], fn: (node: MenuNode) => MenuNode): MenuNode[] =>
  nodes.map(node => fn({ ...node, children: mapTree(node.children, fn) }))

/** Gỡ node ra khỏi cây, trả về cây mới và chính node đó (kèm cả nhánh con). */
export const detachNode = (nodes: MenuNode[], id: number): { tree: MenuNode[]; node: MenuNode | null } => {
  let removed: MenuNode | null = null

  const walk = (list: MenuNode[]): MenuNode[] =>
    list.reduce<MenuNode[]>((acc, node) => {
      if (node.id === id) {
        removed = node
        return acc
      }
      acc.push({ ...node, children: walk(node.children) })
      return acc
    }, [])

  const tree = walk(nodes)
  return { tree, node: removed }
}

export const removeNode = (nodes: MenuNode[], id: number): MenuNode[] => detachNode(nodes, id).tree

export const updateNode = (nodes: MenuNode[], id: number, patch: Partial<Omit<MenuNode, "children">>): MenuNode[] =>
  mapTree(nodes, node => (node.id === id ? { ...node, ...patch } : node))

export const appendNodes = (nodes: MenuNode[], added: MenuNode[]): MenuNode[] => [...nodes, ...added]

/** Thụt vào: node trở thành con cuối của anh liền trước. */
export const indentNode = (nodes: MenuNode[], id: number): MenuNode[] => {
  const found = locate(nodes, id)
  if (!found || found.index === 0) return nodes

  const depth = findDepth(nodes, id)
  const previous = found.siblings[found.index - 1]
  const branchDepth = maxDepthOf(found.siblings[found.index])
  if (depth + 1 + branchDepth > MAX_MENU_DEPTH - 1) return nodes

  const detached = detachNode(nodes, id)
  if (!detached.node) return nodes

  return mapTree(detached.tree, node =>
    node.id === previous.id ? { ...node, children: [...node.children, detached.node as MenuNode] } : node,
  )
}

/** Thụt ra: node trở thành em liền sau của cha nó. */
export const outdentNode = (nodes: MenuNode[], id: number): MenuNode[] => {
  const found = locate(nodes, id)
  if (!found || !found.parent) return nodes

  const detached = detachNode(nodes, id)
  if (!detached.node) return nodes

  const parentId = found.parent.id

  const walk = (list: MenuNode[]): MenuNode[] => {
    const index = list.findIndex(node => node.id === parentId)
    if (index >= 0) {
      const next = [...list]
      next.splice(index + 1, 0, detached.node as MenuNode)
      return next.map(node => ({ ...node, children: walk(node.children) }))
    }
    return list.map(node => ({ ...node, children: walk(node.children) }))
  }

  return walk(detached.tree)
}

/** Đổi chỗ với anh/em liền kề, giữ nguyên cấp. */
export const moveNode = (nodes: MenuNode[], id: number, direction: "up" | "down"): MenuNode[] => {
  const found = locate(nodes, id)
  if (!found) return nodes

  const target = direction === "up" ? found.index - 1 : found.index + 1
  if (target < 0 || target >= found.siblings.length) return nodes

  const reordered = [...found.siblings]
  const [moved] = reordered.splice(found.index, 1)
  reordered.splice(target, 0, moved)

  const replace = (list: MenuNode[]): MenuNode[] => {
    if (list === found.siblings) return reordered
    return list.map(node => ({ ...node, children: replace(node.children) }))
  }

  return replace(nodes)
}

/** Độ sâu của nhánh tính từ node (0 nếu không có con). */
export const maxDepthOf = (node: MenuNode | null | undefined): number => {
  if (!node || node.children.length === 0) return 0
  return 1 + Math.max(...node.children.map(maxDepthOf))
}

/**
 * Thả node vào vị trí mới — dùng cho `onDrop` của antd Tree.
 * `position` < 0 là thả trước node đích, > 0 là sau, `inside` là thành con.
 */
export const dropNode = (
  nodes: MenuNode[],
  dragId: number,
  dropId: number,
  mode: "before" | "after" | "inside",
): MenuNode[] => {
  if (dragId === dropId) return nodes
  // Không cho thả cha vào chính nhánh con của nó.
  const dragNode = findNode(nodes, dragId)
  if (dragNode && findNode(dragNode.children, dropId)) return nodes

  const detached = detachNode(nodes, dragId)
  if (!detached.node) return nodes

  const moved = detached.node

  const walk = (list: MenuNode[]): MenuNode[] => {
    const index = list.findIndex(node => node.id === dropId)

    if (index >= 0) {
      if (mode === "inside") {
        return list.map(node => (node.id === dropId ? { ...node, children: [...node.children, moved] } : node))
      }
      const next = [...list]
      next.splice(mode === "before" ? index : index + 1, 0, moved)
      return next
    }

    return list.map(node => ({ ...node, children: walk(node.children) }))
  }

  return walk(detached.tree)
}

/** Id kế tiếp chưa dùng trong cây — id chỉ cần duy nhất trong phạm vi một menu. */
export const nextNodeId = (existing: MenuNode[]): number => {
  let max = 0
  const walk = (list: MenuNode[]) =>
    list.forEach(node => {
      if (node.id > max) max = node.id
      walk(node.children)
    })
  walk(existing)
  return max + 1
}
